import type {MaterialGraph} from "#material/nodes/base/graph/material-graph"
import {UvMap} from "#material/nodes/input/uv-map/uv-map"
import {materialConnection} from "#material/nodes/base/connection/shorthand"
import {Mapping} from "#material/nodes/vector/mapping/mapping"
import {NormalMap} from "#material/nodes/vector/normal-map/normal-map"
import {BsdfPrincipled} from "#material/nodes/shader/bsdf-principled/bsdf-principled"
import type {InputMaterialConnection, OutputMaterialConnection} from "#material/sockets/definition"
import {MaterialConnection} from "#material/nodes/base/connection/material-connection"
import {add} from "#material/nodes/converter/scalar-math/shorthands"
import {rotateNormalMapVector} from "#material/helpers/rotate-normal-map-vector"
import {TextureSet} from "#material/nodes/texture/texture-set/texture-set"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import {deepTraverseMaterialNodeParameters} from "#material/helpers/deep-traverse-material-node-parameters"
import {BaseGraphNode} from "@cm/graph-v3"

export type UvOffset = {
    horizontal: number
    vertical: number
    rotation: number
}

function offsetUvs(uvConnection: InputMaterialConnection<"point">, offset: UvOffset): OutputMaterialConnection<"point"> {
    return materialConnection(
        new Mapping({
            parameters: {
                inputs: {
                    vector: uvConnection,
                },
                settings: {
                    // Expected platform behavior:
                    //  - Positive horizontal offset shifts the texture to the left
                    //  - Positive vertical offset shifts the texture down
                    //  - Positive rotation rotates texture CCW around unshifted UV origin
                    vectorType: "POINT",
                    location: {x: offset.horizontal, y: offset.vertical, z: 0},
                    rotation: {x: 0, y: 0, z: -offset.rotation * (Math.PI / 180)},
                },
            },
        }),
        "vector",
    )
}

export function transformOffsetUvs(originalGraph: MaterialGraph, offset: UvOffset): MaterialGraph {
    if (offset.horizontal === 0 && offset.vertical === 0 && offset.rotation === 0) {
        return originalGraph
    }

    const clonedGraph = originalGraph.clone({cloneSubNode: () => true})
    for (const connection of clonedGraph.connections) {
        const source = connection.source
        if (source instanceof UvMap) {
            // *** OLD ***
            //                        connection
            // source: UvMap  [uv] ---------------->     connection.destination
            //
            // *** NEW ***
            //                        connection                                           offsetUvs
            // source: UvMap  [uv] ----------------> [vector]    Mapping     [vector] ------------------------->     connection.destination
            const uvChannel = source.parameters.settings.uvMapIndex ?? 0
            if (uvChannel === 0 && connection.destination && connection.destinationParameter) {
                connection.destination.parameters.inputs[connection.destinationParameter] = offsetUvs(connection, offset)
            }
        }
    }

    for (const node of clonedGraph.nodes) {
        if (node instanceof NormalMap) {
            // rotate normal vector in the opposite direction, compared to UV vector
            // (increasing rotation values rotate texture CCW, so normal map should be rotated CW)
            if (node.parameters.inputs.color instanceof MaterialConnection) {
                node.parameters.inputs.color = rotateNormalMapVector(node.parameters.inputs.color, -offset.rotation)
            }
        }

        // the normal map a texture set flattens to rotates by normalRotation, and rotations about the same axis add up
        if (node instanceof TextureSet) {
            node.parameters.settings.normalRotation = (node.parameters.settings.normalRotation ?? 0) - offset.rotation
        }

        if (node instanceof BsdfPrincipled) {
            const anisotropicRotationInput = node.parameters.inputs.anisotropicRotation

            const anisotropicRotationAdjustment = offset.rotation / 360
            if (anisotropicRotationInput instanceof MaterialConnection) {
                node.parameters.inputs.anisotropicRotation = add(anisotropicRotationInput, anisotropicRotationAdjustment)
            } else {
                node.parameters.settings.anisotropicRotation = (node.parameters.settings.anisotropicRotation ?? 0) + anisotropicRotationAdjustment
            }
        }
    }
    return clonedGraph
}

// the one offset that does what these do in turn: each later one goes in next to the UV map, so it acts on the UVs first
export function combineUvOffsets(offsets: UvOffset[]): UvOffset {
    return offsets.reduce(
        (outer, inner) => {
            const angle = (-outer.rotation * Math.PI) / 180
            return {
                horizontal: Math.cos(angle) * inner.horizontal - Math.sin(angle) * inner.vertical + outer.horizontal,
                vertical: Math.sin(angle) * inner.horizontal + Math.cos(angle) * inner.vertical + outer.vertical,
                rotation: outer.rotation + inner.rotation,
            }
        },
        {horizontal: 0, vertical: 0, rotation: 0},
    )
}

// the rotations whose Mapping angle is exactly this one, which reading it back may miss by a bit
const rotationsForMappingAngle = (angle: number) => {
    const estimate = -angle / (Math.PI / 180)
    if (estimate === 0) return [0]
    const value = new Float64Array(1)
    const bits = new BigInt64Array(value.buffer)
    const candidates: number[] = []
    for (let step = -2n; step <= 2n; step++) {
        value[0] = estimate
        bits[0] += step
        if (-value[0] * (Math.PI / 180) === angle) candidates.push(value[0])
    }
    return candidates
}

const cartesianProduct = <T>(choices: T[][]): T[][] =>
    choices.reduce<T[][]>((products, options) => products.flatMap((product) => options.map((option) => [...product, option])), [[]])

// the offsets that turn source into graph, read from the Mappings an offset puts after a UV map and confirmed by making the
// change again, so that any other change gives null
export function uvOffsetsBetween(source: MaterialGraph, graph: MaterialGraph): UvOffset[] | null {
    if (graph.uniqueId === source.uniqueId) return []

    const consumers = new Map<MaterialNode, MaterialNode[]>()
    deepTraverseMaterialNodeParameters(graph.rootNode, (node) => {
        if (!(node instanceof MaterialNode)) return
        for (const input of Object.values(node.parameters.inputs)) {
            if (input instanceof MaterialConnection) consumers.set(input.parameters.value, [...(consumers.get(input.parameters.value) ?? []), node])
        }
    })
    const isOffsetMapping = (node: MaterialNode | undefined): node is Mapping =>
        node instanceof Mapping && node.parameters.settings.vectorType === "POINT" && consumers.get(node)?.length === 1
    const uvMap = [...consumers.keys()].find((node) => node instanceof UvMap && (node.parameters.settings.uvMapIndex ?? 0) === 0)
    const chain: Mapping[] = []
    for (let next = uvMap && consumers.get(uvMap)?.[0]; isOffsetMapping(next); next = consumers.get(next)![0]) chain.push(next)

    const offsetChoices = chain.map(({parameters: {settings}}) =>
        rotationsForMappingAngle(settings.rotation?.z ?? 0).map((rotation) => ({
            horizontal: settings.location?.x ?? 0,
            vertical: settings.location?.y ?? 0,
            rotation,
        })),
    )
    // each offset goes in next to the UV map, so the chain holds the last offset first
    for (let count = 1; count <= chain.length; count++) {
        for (const lastFirst of cartesianProduct(offsetChoices.slice(0, count))) {
            const offsets = lastFirst.reverse()
            if (offsets.reduce(transformOffsetUvs, source).uniqueId === graph.uniqueId) return offsets
        }
    }
    return null
}

const plain = (value: unknown) => JSON.stringify(value, (_, entry: unknown) => (entry instanceof BaseGraphNode ? entry.nodeClass : entry)) ?? "nothing"
const shown = (value: unknown) => (value instanceof MaterialConnection ? `${value.parameters.value.nodeClass}.${value.parameters.key}` : plain(value))
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !(value instanceof BaseGraphNode)

// where graph departs from source, walking both from the output alike, for an error to say what a template changed
export function materialGraphChanges(source: MaterialGraph, graph: MaterialGraph, limit = 5): string[] {
    const changes: string[] = []
    const reached = new Set<BaseGraphNode>()
    const compare = (before: unknown, after: unknown, path: string): void => {
        if (changes.length >= limit) return
        if (before instanceof MaterialConnection && after instanceof MaterialConnection && before.parameters.key === after.parameters.key) {
            compareNodes(before.parameters.value, after.parameters.value, `${path} ← `)
        } else if (before instanceof BaseGraphNode && after instanceof BaseGraphNode && !(after instanceof MaterialConnection)) {
            compareNodes(before, after, path ? `${path} ← ` : "")
        } else if (isRecord(before) && isRecord(after) && Array.isArray(before) === Array.isArray(after)) {
            for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
                compare(before[key], after[key], key === "inputs" || key === "settings" ? path : `${path}.${key}`)
            }
        } else if (plain(before) !== plain(after)) {
            changes.push(`${path}: ${shown(before)} → ${shown(after)}`)
        }
    }
    const compareNodes = (before: BaseGraphNode, after: BaseGraphNode, path: string) => {
        if (before.nodeClass !== after.nodeClass) {
            changes.push(`${path}${before.nodeClass} → ${after.nodeClass}`)
        } else if (!reached.has(after)) {
            reached.add(before).add(after)
            compare(before.parameters, after.parameters, `${path}${after.nodeClass}`)
        }
    }
    compare(source.rootNode, graph.rootNode, "")
    if (changes.length > 0) return changes

    // the hash also covers the graph's own parameters and lists, which hold nodes the output does not reach
    const unreached = (materialGraph: MaterialGraph) =>
        plain(
            materialGraph.nodes
                .filter((node) => !reached.has(node))
                .map((node) => node.nodeClass)
                .sort(),
        )
    const {nodes: _sourceNodes, connections: sourceConnections, ...sourceRest} = source.parameters
    const {nodes: _graphNodes, connections: graphConnections, ...graphRest} = graph.parameters
    compare(sourceRest, graphRest, "MaterialGraph")
    if (unreached(source) !== unreached(graph)) changes.push(`nodes the output does not reach: ${unreached(source)} → ${unreached(graph)}`)
    if (sourceConnections.length !== graphConnections.length) changes.push(`connections: ${sourceConnections.length} → ${graphConnections.length}`)
    return changes
}
